import { Button } from './Button';
import type { ConflictPhase, ConflictRecovery } from './useConflictRecovery';

/** The part of a recovery the notice needs; any `useConflictRecovery` result fits. */
export type ConflictNoticeState = Pick<
  ConflictRecovery<unknown>,
  'conflict' | 'applyAgain' | 'retryRefresh' | 'discard' | 'dismiss'
>;

export interface ConflictNoticeProps {
  /** What `useConflictRecovery` returned. */
  readonly recovery: ConflictNoticeState;
  /** What changed, as the start of a sentence: "This price", "The workspace settings". */
  readonly subject?: string;
}

function messageFor(phase: ConflictPhase, subject: string): string {
  switch (phase) {
    case 'refreshing':
      return `${subject} was changed by someone else. Loading the latest version…`;
    case 'ready':
      return `${subject} was changed by someone else while you were editing. The latest version is shown, and your changes are kept.`;
    case 'failed':
      return `${subject} was changed by someone else, and the latest version could not be loaded.`;
  }
}

/**
 * Says, in the standard words, that a save was refused because someone else
 * saved first, and offers the way forward.
 *
 * The same wording and the same two buttons on every screen is the point: a
 * dispatcher who has met this once knows what it means everywhere. It renders
 * nothing until there is a conflict, and announces itself when there is.
 *
 * @param props see {@link ConflictNoticeProps}
 * @returns the notice, or nothing when there is no conflict
 */
export function ConflictNotice({
  recovery,
  subject = 'This record',
}: ConflictNoticeProps): React.JSX.Element | null {
  const { conflict } = recovery;
  if (conflict === null) {
    return null;
  }

  return (
    <div className="fo-conflict" role="alert">
      <p className="fo-conflict__message">{messageFor(conflict.phase, subject)}</p>
      {conflict.phase === 'ready' ? (
        <div className="fo-conflict__actions">
          <Button variant="primary" onClick={recovery.applyAgain}>
            Apply my changes again
          </Button>
          <Button variant="ghost" onClick={recovery.discard}>
            Keep the latest version
          </Button>
        </div>
      ) : null}
      {conflict.phase === 'failed' ? (
        <div className="fo-conflict__actions">
          <Button onClick={recovery.retryRefresh}>Try again</Button>
          <Button variant="ghost" onClick={recovery.dismiss}>
            Keep editing
          </Button>
        </div>
      ) : null}
    </div>
  );
}
